import { API_CONFIG } from "../Utils/APIConfig";

class ExternalAPI {
  constructor() {
    this.apiEndpoints = {
      get: `${API_CONFIG.BASE_URL}/External.asmx/GetData`,
      set: `${API_CONFIG.BASE_URL}/External.asmx/SendChange`,
      // Keep these if you still need them for other purposes
      systemChange: `${API_CONFIG.BASE_URL}/Set.asmx/SendSystemChange`,
      access: `${API_CONFIG.BASE_URL}/Access.asmx/SendChange`
    };
  }

  /**
   * Call the GetData API of External.asmx
   * @param {number} moduleID - Module ID (7 for Touchpoint/Feedback)
   * @param {string} actionType - Action type (e.g., 'feedbackDetails')
   * @param {any} data - The data payload for the API
   * @param {object} options - Optional extra parameters
   * @returns {Promise<any>} - API response
   */
  async getData(moduleID, actionType, data, options = {}) {
    try {
      const parameters = this.prepareParameters(moduleID, actionType, data, options);
      const result = await this.fetchAPI(this.apiEndpoints.get, JSON.stringify(parameters));
      
      // Optional: If your API returns a DataSet JSON string in updatePackage
      if (result?.updatePackage) {
        try {
          result.parsedDataSet = JSON.parse(result.updatePackage);
        } catch (err) {
          console.warn('Failed to parse updatePackage JSON', err);
        }
      }
      return result;
    } catch (error) {
      console.error('Error in getData:', error);
      throw error;
    }
  }

  /**
   * Call the SendChange API of External.asmx
   * @param {number} moduleID - Module ID (8 for Touchpoint/Feedback)
   * @param {string} actionType - Action type (e.g., 'setFeedback')
   * @param {any} data - The data payload for the API
   * @param {object} options - Optional extra parameters (UserID, Token, ForAgencyID, etc.)
   * @returns {Promise<any>} - API response
   */
  async sendChange(moduleID, actionType, data, options = {}) {
    try {
      const parameters = this.prepareParameters(moduleID, actionType, data, options);
      const result = await this.fetchAPI(this.apiEndpoints.set, JSON.stringify(parameters));
      
      // Optional: Parse updatePackage if present
      if (result?.updatePackage) {
        try {
          result.parsedDataSet = JSON.parse(result.updatePackage);
        } catch (err) {
          console.warn('Failed to parse updatePackage JSON', err);
        }
      }
      return result;
    } catch (error) {
      console.error('Error in sendChange:', error);
      throw error;
    }
  }

  /**
   * Helper method specifically for getting agency vitals
   * Optional visitorInfo: { name, mobile, cc, company, email, changeType }
   */
  async getAgencyVitals(agencyID, visitorInfo = null, options = {}) {
    const dataObj = {
      agencyID: agencyID
    };

    if (visitorInfo && typeof visitorInfo === "object") {
      if (visitorInfo.name) dataObj.name = visitorInfo.name;
      if (visitorInfo.mobile) dataObj.mobile = visitorInfo.mobile;
      if (visitorInfo.cc) dataObj.cc = visitorInfo.cc;
      if (visitorInfo.company) dataObj.company = visitorInfo.company;
      if (visitorInfo.email) dataObj.email = visitorInfo.email;
      if (visitorInfo.changeType) dataObj.changeType = visitorInfo.changeType;
      if (visitorInfo.visitorAgencyID) dataObj.visitorAgencyID = visitorInfo.visitorAgencyID;
      if (visitorInfo.visitorAgencyName) dataObj.visitorAgencyName = visitorInfo.visitorAgencyName;
      if (visitorInfo.visitorUserID) dataObj.visitorUserID = visitorInfo.visitorUserID;
    }

    const data = [dataObj];
    
    return await this.getData(4, 'get', data, options);
  }

  /**
   * Submit webstore inquiry to agency via thr_SetExt
   */
  async sendWebstoreInquiry(inquiryData, options = {}) {
    const data = [
      {
        agencyID: inquiryData.agencyID || inquiryData.m,
        company: inquiryData.company || inquiryData.bizName || "-",
        name: inquiryData.name || inquiryData.contactName || "",
        mobile: inquiryData.mobile || "",
        cc: inquiryData.cc || "91",
        email: inquiryData.email || "",
        product: inquiryData.product || "",
        needs: inquiryData.needs || inquiryData.notes || "",
        source: inquiryData.source || inquiryData.src || "SahiClub",
        agencyName: inquiryData.agencyName || inquiryData.storeName || "",
        ownerMobile: inquiryData.ownerMobile || inquiryData.agencyMobile || "",
        ownerName: inquiryData.ownerName || inquiryData.agencyName || "",
        ownerCC: inquiryData.ownerCC || inquiryData.ownerCc || "91",
        calledFrom: inquiryData.calledFrom || ""
      }
    ];
    
    return await this.sendChange(6, "set", data, options);
  }

  /**
   * Helper method specifically for feedback operations
   */
  async getFeedbackDetails(threadData, type, options = {}) {
    const data = [
      {
        agencyID: threadData?.agencyID,
        threadID: threadData?.threadID,
        type
      }
    ];
    
    return await this.getData(1, "get", data, options);
  }

  /**
   * Helper method specifically for setting feedback
   */
  async setFeedbackDetails(feedbackData, type, options = {}) {
    const data = [
      {
        agencyID: feedbackData?.agencyID,
        connID: feedbackData?.connID,
        threadID: feedbackData?.threadID,
        fdbkScore: feedbackData?.fdbkScore,
        fdbkNotes: feedbackData?.fdbkNotes,
        rejReason: feedbackData?.rejReason,
        type
      }
    ];
    
    return await this.sendChange(1, "set", data, options);
  }

  async getConnectionFeedback(connData, type, options = {}) {
    const data = [
      {
        agencyID: connData?.agencyID,
        connID: connData?.connID,
        type
      }
    ];

    return await this.getData(2, "get", data, options);
  }

  async setConnectionFeedback(connData, options = {}) {
    const data = [
      {
        agencyID: connData?.agencyID,
        connID: connData?.connID,
        type: connData?.type,
        fdbkScore: connData?.fdbkScore,
        fdbkNotes: connData?.fdbkNotes,
        agencyName: connData?.agencyName || "",
        agencyEmail: "",
        contactPerson: connData?.contactPerson || "",
        fdbkTxt: connData?.fdbkTxt || "",
        bizName: connData?.bizName || "",
        connMobile: connData?.connMobile || "",
        connEmail: connData?.connEmail || ""
      }
    ];

    return await this.sendChange(2, "set", data, options);
  }

  async completeTask(agencyID, connID, taskID, options = {}) {
    const data = [
      {
        agencyID: agencyID,
        connID: connID,
        taskID: taskID,
        type: 'COMPLETE'
      }
    ];

    return await this.sendChange(5, "set", data, options);
  }

  async cancelTask(agencyID, connID, taskID, options = {}) {
    const data = [
      {
        agencyID: agencyID,
        connID: connID,
        taskID: taskID,
        type: 'CANCEL'
      }
    ];

    return await this.sendChange(5, "set", data, options);
  }

  async createContactLog(agencyID, connID, logType, taskID, options = {}) {
    const contactLogType = logType === 'Call' ? 'Call_Log' : 'Message_Log';

    const data = [
      {
        agencyID: agencyID,
        connID: connID,
        taskID: taskID,
        type: contactLogType,
      }
    ];

    return await this.sendChange(2, "action", data, options);
  }

  // Helper to prepare API parameters (simplified for external feedback API)
  prepareParameters(moduleID, actionType, data, options = {}) {
    return [
      {
        ModuleID: moduleID,
        ActionType: actionType,
        Data: data,
        ...options
      }
    ];
  }

  // Helper to make the fetch POST request
  async fetchAPI(endpoint, body) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify({ parameters: body })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (!result?.d) {
      throw new Error('Invalid response format');
    }

    return result.d;
  }
}

// Create singleton instance
const externalAPI = new ExternalAPI();
export default externalAPI;